import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { FileText, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { trackCitationClick } from '@/lib/citation-tracking'

// ===================
// Types
// ===================

export interface CitationSource {
  documentId: string
  documentName: string
  pageNumber: number | null
  snippet?: string
  highlightData?: unknown
  /** Manual quality tier override (PR 7). 'gold' shows a star indicator. */
  qualityTier?: string | null
}

interface CitationBadgeProps {
  citationNumber: number
  source?: CitationSource | undefined
  onNavigate?: ((source: CitationSource) => void) | undefined
  className?: string
}

// ===================
// CitationBadge Component
// ===================

/**
 * Inline citation badge rendered as [N].
 * Shows document info on hover and navigates to source on click.
 */
export function CitationBadge({
  citationNumber,
  source,
  onNavigate,
  className,
}: CitationBadgeProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleClick = () => {
    if (source && onNavigate) {
      trackCitationClick({
        documentId: source.documentId,
        documentName: source.documentName,
        citationIndex: citationNumber,
        pageNumber: source.pageNumber,
        context: 'drafting',
      })
      onNavigate(source)
    }
  }

  // If no source info, just render a simple badge
  if (!source) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center',
          'bg-muted text-muted-foreground text-xs font-medium',
          'rounded px-1.5 py-0.5 mx-0.5',
          'select-none',
          className
        )}
      >
        [{citationNumber}]
      </span>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            className={cn(
              'inline-flex items-center justify-center',
              'bg-muted hover:bg-muted/80 text-muted-foreground text-xs font-medium',
              'rounded px-1.5 py-0.5 mx-0.5',
              'cursor-pointer transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
              className
            )}
          >
            [{citationNumber}]
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-xs p-0 bg-card text-card-foreground border shadow-lg"
        >
          <div className="p-3 space-y-2">
            {/* Document name */}
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium truncate">
                {source.documentName}
              </span>
            </div>

            {/* Page number. Ternary, not `&&` — a page-less source (any .docx)
                arrives as 0, and `0 && …` renders a bare "0". */}
            {source.pageNumber ? (
              <div className="text-meta">
                Page {source.pageNumber}
              </div>
            ) : null}

            {/* Snippet preview */}
            {source.snippet && (
              <p className="text-meta line-clamp-3 border-l-2 border-muted pl-2">
                "{source.snippet.slice(0, 150)}
                {source.snippet.length > 150 ? '...' : ''}"
              </p>
            )}

            {/* View button */}
            {onNavigate && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs"
                onClick={handleClick}
              >
                <ExternalLink className="h-3 w-3 mr-1.5" />
                View in document
              </Button>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ===================
// Citation Parser Utility
// ===================

/**
 * Parse citation markers from text and return parts for rendering.
 * Used to convert "[1][2] Some text [3]" into renderable components.
 */
export interface TextPart {
  type: 'text' | 'citation'
  content: string
  citationNumber?: number
}

export function parseCitationsInText(text: string): TextPart[] {
  const parts: TextPart[] = []
  const citationPattern = /\[(\d+)\]/g
  let lastIndex = 0
  let match

  while ((match = citationPattern.exec(text)) !== null) {
    // Add text before citation
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      })
    }

    // Add citation
    parts.push({
      type: 'citation',
      content: match[0],
      citationNumber: parseInt(match[1] ?? '0', 10),
    })

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    })
  }

  return parts
}

// ===================
// TextWithCitations Component
// ===================

interface TextWithCitationsProps {
  text: string
  sources?: CitationSource[]
  onNavigateToSource?: (source: CitationSource) => void
  className?: string
}

/**
 * Renders text with inline citation badges.
 * Parses [N] markers and replaces them with CitationBadge components.
 */
export function TextWithCitations({
  text,
  sources = [],
  onNavigateToSource,
  className,
}: TextWithCitationsProps) {
  const parts = parseCitationsInText(text)

  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === 'text') {
          return <span key={index}>{part.content}</span>
        }

        const citationNumber = part.citationNumber!
        // Sources are 1-indexed in the text, 0-indexed in the array
        const source = sources[citationNumber - 1]

        return (
          <CitationBadge
            key={index}
            citationNumber={citationNumber}
            source={source}
            onNavigate={onNavigateToSource}
          />
        )
      })}
    </span>
  )
}

export default CitationBadge
